import React, { useMemo, useContext, useState } from "react";
import PropTypes from "prop-types";

import {
  Textarea,
  Title,
  Column,
  Button,
  Field,
  Control,
  Label,
  Input
} from "rbx";

import {
  BooleanInput,
  SystemCodeSelect,
  StateSelect,
  SocialSecurityInput,
  EmployeeSelect,
  DateInput,
  PhoneNumberInput,
  LocationSelect
} from "../inputs";

import Steps from "../Steps";

import { ModalContext } from "../ModalProvider";

const EmployeeForm = ({ inputs, onChange, onSubmit }) => {
  const { setModalOpen } = useContext(ModalContext);
  const [showDriver, setShowDriver] = useState(false);

  const EMPLOYEE_STEPS = useMemo(() => {
    const handleChange = e => {
      if (e.target.name === "EmployeeTypeRecIdRef") {
        setShowDriver(e.target.value === "34");
        onChange(e);
      } else {
        onChange(e);
      }
    };
    return [
      {
        title: "Personal",
        content: (
          <React.Fragment>
            <Field kind="group">
              <Control expanded>
                <Label>First Name</Label>
                <Input
                  required
                  autocomplete="new"
                  name="FirstName"
                  type="text"
                  value={inputs.FirstName}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Middle Name</Label>
                <Input
                  autocomplete="new"
                  name="MiddleName"
                  type="text"
                  value={inputs.MiddleName}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Last Name</Label>
                <Input
                  required
                  autocomplete="new"
                  name="LastName"
                  type="text"
                  value={inputs.LastName}
                  onChange={handleChange}
                />
              </Control>

              <Control>
                <Label>Active</Label>
                <BooleanInput
                  name="Active"
                  value={inputs.Active}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            <Field kind="group">
              <Control expanded>
                <Label>Address 1</Label>
                <Input
                  autocomplete="new"
                  name="Address1"
                  type="text"
                  value={inputs.Address1}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Address 2</Label>
                <Input
                  autocomplete="new"
                  name="Address2"
                  type="text"
                  value={inputs.Address2}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            <Field kind="group">
              <Control expanded>
                <Label>City</Label>
                <Input
                  autocomplete="new"
                  name="City"
                  type="text"
                  value={inputs.City}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>State</Label>
                <StateSelect
                  name="State"
                  value={inputs.State}
                  onChange={handleChange}
                />
              </Control>
              <Control>
                <Label>Zip</Label>
                <Input
                  autocomplete="new"
                  name="ZipCode"
                  type="text"
                  value={inputs.ZipCode}
                  onChange={handleChange}
                />
              </Control>
              <Control>
                <Label>Country</Label>
                <Input
                  autocomplete="new"
                  name="Country"
                  type="text"
                  value={inputs.Country}
                  onChange={handleChange}
                />
              </Control>
            </Field>
          </React.Fragment>
        )
      },
      {
        title: "Contact",
        content: (
          <React.Fragment>
            <Field kind="group">
              <Control expanded>
                <Label>Home #</Label>
                <PhoneNumberInput
                  name="HomePhoneNo"
                  value={inputs.HomePhoneNo}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Cell #</Label>
                <PhoneNumberInput
                  name="CellPhoneNo"
                  value={inputs.CellPhoneNo}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Alternate #</Label>
                <PhoneNumberInput
                  name="AlternatePhoneNo"
                  value={inputs.AlternatePhoneNo}
                  onChange={handleChange}
                />
              </Control>
            </Field>

            <Field kind="group">
              <Control expanded>
                <Label>Email 1</Label>
                <Input
                  autocomplete="new"
                  name="Email1"
                  type="email"
                  value={inputs.Email1}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Email 2</Label>
                <Input
                  autocomplete="new"
                  name="Email2"
                  type="email"
                  value={inputs.Email2}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            <Field>
              <Control>
                <SystemCodeSelect
                  icon="address-book"
                  label="Contact Preference"
                  lookup={{
                    Category: "Employee",
                    Code: "ContactPreference"
                  }}
                  name="ContactPreferenceRecIdRef"
                  value={inputs.ContactPreferenceRecIdRef}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            <hr style={{ backgroundColor: "hsl(0, 0%, 86%)" }} />
            <Field kind="group">
              <Control expanded>
                <Label>Emergency #</Label>
                <PhoneNumberInput
                  name="EmergencyPhoneNo"
                  value={inputs.EmergencyPhoneNo}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Emergency Contact</Label>
                <Input
                  autocomplete="new"
                  name="EmergencyContact"
                  type="text"
                  value={inputs.EmergencyContact}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Emergency Relationship</Label>
                <Input
                  autocomplete="new"
                  name="EmergencyRelationship"
                  type="text"
                  value={inputs.EmergencyRelationship}
                  onChange={handleChange}
                />
              </Control>
            </Field>
          </React.Fragment>
        )
      },
      {
        title: "Payroll",
        content: (
          <React.Fragment>
            <Field kind="group">
              <Control>
                <Label>Payroll Id</Label>
                <Input
                  autocomplete="new"
                  name="PayrollId"
                  type="text"
                  value={inputs.PayrollId}
                  onChange={handleChange}
                />
              </Control>
              <Control>
                <Label>Labor Cost</Label>
                <Input
                  autoComplete="new"
                  defaultValue="$0.00"
                  name="HourlyRate"
                  type="text"
                  value={inputs.HourlyRate}
                  onChange={onChange}
                />
              </Control>
              <Control expanded>
                <Label>Job Title</Label>
                <Input
                  autocomplete="new"
                  name="JobTitle"
                  type="text"
                  value={inputs.JobTitle}
                  onChange={handleChange}
                />
              </Control>
              <Control>
                <Label>Veteran</Label>
                <BooleanInput
                  name="Veteran"
                  value={inputs.Veteran}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            <Field kind="group">
              <Control>
                <Label>SSN</Label>
                <SocialSecurityInput
                  name="SocialSecurityNo"
                  value={inputs.SocialSecurityNo}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <SystemCodeSelect
                  required
                  icon="globe"
                  label="Employee Type"
                  lookup={{
                    Category: "Employee",
                    Code: "EmployeeType"
                  }}
                  name="EmployeeTypeRecIdRef"
                  value={inputs.EmployeeTypeRecIdRef}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <EmployeeSelect
                  icon="globe"
                  label="Supervisor"
                  name="SupervisorRecIdRef"
                  value={inputs.SupervisorRecIdRef}
                  variables={{ where: { EmployeeTypeRecIdRef: 96 } }}
                  onChange={handleChange} // Executive Level
                />
              </Control>
            </Field>
            <Field kind="group">
              <Control expanded>
                <Label>Birth Date</Label>
                <DateInput
                  name="BirthDate"
                  value={inputs.BirthDate}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Hire Date</Label>
                <DateInput
                  name="HireDate"
                  value={inputs.HireDate}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            <Field>
              <Label>Payroll Note</Label>
              <Textarea
                name="PayrollNote"
                value={inputs.PayrollNote}
                onChange={handleChange}
              />
            </Field>
          </React.Fragment>
        )
      },
      {
        title: "Vacation",
        disabled: showDriver,
        content: (
          <React.Fragment>
            <Field>
              <Title size={4}>Vacation</Title>
            </Field>
            <Field kind="group">
              <Control expanded>
                <Label>Days Allowed</Label>
                <Input
                  autocomplete="new"
                  name="VacationDaysAllowedThisYear"
                  type="number"
                  value={inputs.VacationDaysAllowedThisYear}
                  onChange={handleChange}
                />
              </Control>

              <Control expanded>
                <Label>Days Used</Label>
                <Input
                  autocomplete="new"
                  name="VacationDaysUsedThisYear"
                  type="number"
                  value={inputs.VacationDaysUsedThisYear}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Renewal Date</Label>
                <DateInput
                  name="VacationDaysRenewalDate"
                  value={inputs.VacationDaysRenewalDate}
                  onChange={handleChange}
                />
              </Control>
            </Field>
            <Field>
              <Title size={4}>Personal</Title>
            </Field>
            <Field kind="group">
              <Control expanded>
                <Label>Days Allowed</Label>
                <Input
                  autocomplete="new"
                  name="PersonalDaysAllowedThisYear"
                  type="number"
                  value={inputs.PersonalDaysAllowedThisYear}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Days Used</Label>
                <Input
                  autocomplete="new"
                  name="PersonalDaysUsedThisYear"
                  type="number"
                  value={inputs.PersonalDaysUsedThisYear}
                  onChange={handleChange}
                />
              </Control>
              <Control expanded>
                <Label>Renewal Date</Label>
                <DateInput
                  name="PersonalDaysRenewalDate"
                  value={inputs.PersonalDaysRenewalDate}
                  onChange={handleChange}
                />
              </Control>
            </Field>
          </React.Fragment>
        )
      },
      {
        title: "Driver",
        content: (
          <React.Fragment>
            <Column.Group>
              <Column>
                <Field kind="group">
                  <Control expanded>
                    <Label>Driver Flag</Label>
                    <BooleanInput
                      name="DriverFlag"
                      value={inputs.DriverFlag}
                      onChange={handleChange}
                    />
                  </Control>
                  <Control expanded>
                    <Label>Drug Test</Label>
                    <BooleanInput
                      disabled={!inputs.DriverFlag}
                      name="DrugTest"
                      value={inputs.DrugTest}
                      onChange={handleChange}
                    />
                  </Control>
                  <Control expanded>
                    <Label>Shop Steward</Label>
                    <BooleanInput
                      disabled={!inputs.DriverFlag}
                      name="ShopSteward"
                      value={inputs.ShopSteward}
                      onChange={handleChange}
                    />
                  </Control>
                </Field>
              </Column>
              <Column size={9}>
                <Field>
                  <Control>
                    <LocationSelect
                      disabled={!inputs.DriverFlag}
                      icon="globe"
                      label="Home Terminal"
                      name="HomeCompanyLocationId"
                      value={inputs.HomeCompanyLocationId}
                      onChange={handleChange}
                    />
                  </Control>
                </Field>
              </Column>
            </Column.Group>
            <Column.Group>
              <Column size={3}>
                <Field>
                  <Control>
                    <Label>Seniority Date</Label>
                    <DateInput
                      disabled={!inputs.DriverFlag}
                      name="SeniorityDate"
                      value={inputs.SeniorityDate}
                      onChange={handleChange}
                    />
                  </Control>
                </Field>
              </Column>
              <Column size={3}>
                <Field>
                  <Control>
                    <Label>Seniority #</Label>
                    <Input
                      autocomplete="new"
                      disabled={!inputs.DriverFlag}
                      name="SeniorityNo"
                      type="text"
                      value={inputs.SeniorityNo}
                      onChange={handleChange}
                    />
                  </Control>
                </Field>
              </Column>
              <Column size={3}>
                <Field>
                  <Control>
                    <Label>Weight</Label>
                    <Input
                      autocomplete="new"
                      disabled={!inputs.DriverFlag}
                      name="Weight"
                      type="text"
                      value={inputs.Weight}
                      onChange={handleChange}
                    />
                  </Control>
                </Field>
              </Column>
              <Column size={3}>
                <Field>
                  <Control>
                    <Label>Last Worked Date</Label>
                    <DateInput
                      disabled={!inputs.DriverFlag}
                      name="LastWorkedDate"
                      value={inputs.LastWorkedDate}
                      onChange={handleChange}
                    />
                  </Control>
                </Field>
              </Column>
            </Column.Group>
            <Column.Group>
              <Column size={6}>
                <Field>
                  <Control>
                    <Label>License #</Label>
                    <Input
                      autocomplete="new"
                      disabled={!inputs.DriverFlag}
                      name="DriverLicenseNo"
                      type="text"
                      value={inputs.DriverLicenseNo}
                      onChange={handleChange}
                    />
                  </Control>
                </Field>
              </Column>
              <Column size={6}>
                <Control expanded>
                  <Label>License State</Label>
                  <StateSelect
                    disabled={!inputs.DriverFlag}
                    name="DriverLicenseState"
                    value={inputs.DriverLicenseState}
                    onChange={handleChange}
                  />
                </Control>
              </Column>
            </Column.Group>
          </React.Fragment>
        )
      },
      {
        title: "Confirm",
        content: (
          <div className="confirm-container">
            <Button color="success" size="large" onClick={onSubmit}>
              Create Employee
            </Button>
          </div>
        )
      }
    ];
  }, [
    inputs.Active,
    inputs.Address1,
    inputs.Address2,
    inputs.AlternatePhoneNo,
    inputs.BirthDate,
    inputs.CellPhoneNo,
    inputs.City,
    inputs.ContactPreferenceRecIdRef,
    inputs.Country,
    inputs.DriverFlag,
    inputs.DriverLicenseNo,
    inputs.DriverLicenseState,
    inputs.DrugTest,
    inputs.Email1,
    inputs.Email2,
    inputs.EmergencyContact,
    inputs.EmergencyPhoneNo,
    inputs.EmergencyRelationship,
    inputs.EmployeeTypeRecIdRef,
    inputs.FirstName,
    inputs.HireDate,
    inputs.HomeCompanyLocationId,
    inputs.HomePhoneNo,
    inputs.HourlyRate,
    inputs.JobTitle,
    inputs.LastName,
    inputs.LastWorkedDate,
    inputs.MiddleName,
    inputs.PayrollId,
    inputs.PayrollNote,
    inputs.PersonalDaysAllowedThisYear,
    inputs.PersonalDaysRenewalDate,
    inputs.PersonalDaysUsedThisYear,
    inputs.SeniorityDate,
    inputs.SeniorityNo,
    inputs.ShopSteward,
    inputs.SocialSecurityNo,
    inputs.State,
    inputs.SupervisorRecIdRef,
    inputs.VacationDaysAllowedThisYear,
    inputs.VacationDaysRenewalDate,
    inputs.VacationDaysUsedThisYear,
    inputs.Veteran,
    inputs.Weight,
    inputs.ZipCode,
    onChange,
    onSubmit,
    showDriver
  ]);
  return (
    <Steps
      defaultStep={0}
      steps={EMPLOYEE_STEPS}
      onCancel={() => setModalOpen(false, "")}
    />
  );
};

EmployeeForm.propTypes = {
  inputs: PropTypes.object,
  onChange: PropTypes.func,

  onSubmit: PropTypes.func
};
EmployeeForm.defaultProps = {
  inputs: {}
};
export default EmployeeForm;
